import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuthController, useDataSource } from "@firecms/core";
import {
  BusinessIcon,
  Button,
  Card,
  CircularProgress,
  LinkIcon,
  SettingsIcon,
  Typography,
} from "@firecms/ui";
import { FirebaseError } from "firebase/app";
import logoUrl from "../assets/logo.svg";
import {
  acceptEventInvitation,
  acceptAccountInvitation,
  getMyPendingAccountInvitations,
  getMyPendingEventInvitations,
  type PendingAccountInvitation,
  type PendingEventInvitation,
} from "../firebase/invitations";
import { getMyPermissions } from "../firebase/getMyPermissions";
import {
  accountIdFromRef,
  eventIdsFromRef,
  listMyAccountMemberships,
  listMyEventMemberships,
  MANAGER_MEMBERSHIP_ROLE,
} from "../firebase/membershipQueries";
import { mergeAuthExtras, type AuthExtras } from "../types/auth";
import {
  buildAccountPath,
  buildEventPath,
  buildGlobalPath,
} from "../workspace/workspaceRoutes";

type HubAccount = { id: string; name: string };
type HubEvent = { accountId: string; eventId: string; label: string };

const hubMuted = "text-surface-600 dark:text-surface-400";
const hubBody = "text-surface-900 dark:text-surface-100";
const hubCard =
  "overflow-hidden rounded-2xl border border-surface-200/90 bg-surface-50 shadow-sm transition-all dark:border-surface-700 dark:bg-surface-800";
const hubAcceptButton =
  "w-full min-w-[7.5rem] shrink-0 !rounded-xl !border-0 !bg-[var(--color-primary)] !px-5 !py-2.5 !text-sm !font-semibold !normal-case !tracking-normal !text-white !shadow-md transition hover:!brightness-110 focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-offset-2 focus-visible:!outline-[var(--color-primary)] disabled:!cursor-not-allowed disabled:!opacity-50 sm:!w-auto";

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden
    >
      <path
        fillRule="evenodd"
        d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function MailIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}

function HubIconBadge({
  children,
  tone = "primary",
}: {
  children: React.ReactNode;
  tone?: "primary" | "amber" | "neutral";
}) {
  const toneClass =
    tone === "amber"
      ? "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300"
      : tone === "neutral"
        ? "bg-surface-200/80 text-surface-600 dark:bg-surface-700 dark:text-surface-300"
        : "bg-primary/10 text-primary dark:bg-primary/20";

  return (
    <span
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${toneClass}`}
    >
      {children}
    </span>
  );
}

function HubStatPill({
  label,
  value,
}: {
  label: string;
  value: number;
}) {
  if (value <= 0) {
    return null;
  }

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-surface-200 bg-white/80 px-3 py-1 text-xs font-medium text-surface-700 shadow-sm backdrop-blur-sm dark:border-surface-600 dark:bg-surface-800/80 dark:text-surface-200">
      <span className="font-semibold tabular-nums text-primary">{value}</span>
      {label}
    </span>
  );
}

function HubSection({
  title,
  description,
  badge,
  children,
}: {
  title: string;
  description?: string;
  badge?: number;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3 border-b border-surface-200 pb-3 dark:border-surface-700">
        <div>
          <div className="flex items-center gap-2.5">
            <h2 className={`text-base font-semibold tracking-tight ${hubBody}`}>
              {title}
            </h2>
            {badge != null && badge > 0 ? (
              <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-[11px] font-bold tabular-nums text-white">
                {badge}
              </span>
            ) : null}
          </div>
          {description ? (
            <p className={`mt-1 text-sm leading-relaxed ${hubMuted}`}>
              {description}
            </p>
          ) : null}
        </div>
      </div>
      {children}
    </section>
  );
}

function HubLinkItem({
  to,
  label,
  icon,
}: {
  to: string;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className={`group flex items-center gap-3.5 rounded-2xl border border-surface-200/90 bg-surface-50 px-4 py-3.5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/35 hover:bg-white hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary dark:border-surface-700 dark:bg-surface-800 dark:hover:border-primary/45 dark:hover:bg-surface-700 ${hubBody}`}
    >
      <HubIconBadge tone="neutral">{icon}</HubIconBadge>
      <span className="min-w-0 flex-1 text-sm font-medium leading-snug">
        {label}
      </span>
      <ChevronRightIcon
        className={`h-5 w-5 shrink-0 transition-transform group-hover:translate-x-0.5 ${hubMuted}`}
      />
    </Link>
  );
}

function HubLoadingSkeleton() {
  return (
    <div className="mx-auto w-full max-w-3xl animate-pulse px-4 py-10 pb-20 sm:px-6">
      <div className="mb-8 h-36 rounded-2xl bg-surface-200/70 dark:bg-surface-800" />
      <div className="flex flex-col gap-4">
        <div className="h-24 rounded-2xl bg-surface-200/60 dark:bg-surface-800/80" />
        <div className="h-24 rounded-2xl bg-surface-200/60 dark:bg-surface-800/80" />
      </div>
    </div>
  );
}

function hubLoadErrorMessage(error: unknown): string {
  if (error instanceof FirebaseError) {
    if (error.code === "functions/failed-precondition") {
      const detail = error.message.toLowerCase();
      if (detail.includes("email must be verified")) {
        return "Debés verificar tu correo antes de ver o aceptar invitaciones.";
      }
      if (detail.includes("user email is required")) {
        return "Tu cuenta no tiene un correo asociado. Usá Google o email/contraseña con correo.";
      }
    }
    if (error.code === "functions/unauthenticated") {
      return "La sesión expiró. Volvé a iniciar sesión.";
    }
    if (error.code === "functions/not-found") {
      return "No se encontró el servicio de membresías. ¿Están desplegadas las Cloud Functions?";
    }
    if (error.code === "functions/internal") {
      return "No pudimos cargar tus accesos. Probá de nuevo en unos segundos.";
    }
  }

  return "No pudimos cargar tus accesos. Revisá la consola o intentá de nuevo.";
}

function getHubIntroCopy({
  hasInvitations,
  hasPanels,
  isEmpty,
}: {
  hasInvitations: boolean;
  hasPanels: boolean;
  isEmpty: boolean;
}): string[] {
  if (isEmpty) {
    return [
      "Este es tu punto de partida en el admin: acá ves invitaciones y accesos según tu usuario.",
      "Por ahora no hay accesos ni invitaciones con este correo. Si te acaban de invitar, verificá que iniciaste sesión con el mail correcto y usá el botón Actualizar más abajo para volver a consultar.",
    ];
  }

  if (hasInvitations && !hasPanels) {
    return [
      "Todavía no tenés permisos para ingresar al administrador con este usuario.",
      "Sí tenés invitaciones pendientes: revisá quién te invitó y aceptá las que quieras. Al aceptar una, vas a poder acceder al panel correspondiente desde acá.",
    ];
  }

  if (hasPanels && !hasInvitations) {
    return [
      "Ya podés ingresar al administrador: tenés uno o más espacios habilitados.",
      "Elegí abajo el que quieras usar hoy.",
    ];
  }

  if (hasInvitations && hasPanels) {
    return [
      "Ya tenés acceso al administrador y también invitaciones pendientes.",
      "Entrá a los paneles habilitados más abajo, o aceptá primero las invitaciones que te interesen.",
    ];
  }

  return [
    "Este es tu punto de partida en TeamOps Admin.",
    "Revisá qué hay disponible para vos abajo.",
  ];
}

function eventRoleLabel(role: PendingEventInvitation["role"]): string {
  switch (role) {
    case "manager":
      return "manager";
    case "operator":
      return "operador";
    case "presenter":
      return "presentador";
    case "participant":
      return "participante";
    default:
      return "miembro";
  }
}

function formatAccountInvitationCopy(invitation: PendingAccountInvitation): {
  message: string;
  hint: string;
} {
  const who = invitation.inviterName ?? "Alguien de tu equipo";
  const account = invitation.accountName ?? "una cuenta";
  return {
    message: `${who} te invitó a administrar la cuenta ${account}.`,
    hint: "Si aceptás, vas a poder entrar al panel de esa cuenta desde acá.",
  };
}

function formatEventInvitationCopy(invitation: PendingEventInvitation): {
  message: string;
  hint: string;
} {
  const who = invitation.inviterName ?? "Alguien de tu equipo";
  const account = invitation.accountName ?? "una cuenta";
  const event = invitation.eventName ?? "un evento";
  const role = eventRoleLabel(invitation.role);
  return {
    message: `${who} te invitó como ${role} al evento ${event} en ${account}.`,
    hint: "Al aceptar, obtenés acceso a ese evento con el rol de la invitación.",
  };
}

export function AdminHub() {
  const authController = useAuthController();
  const dataSource = useDataSource();
  const extra = authController.extra as AuthExtras | undefined;
  const userId = authController.user?.uid;

  const [accountInvitations, setAccountInvitations] = useState<
    PendingAccountInvitation[]
  >([]);
  const [eventInvitations, setEventInvitations] = useState<
    PendingEventInvitation[]
  >([]);
  const [accounts, setAccounts] = useState<HubAccount[]>([]);
  const [events, setEvents] = useState<HubEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);

  const displayName = authController.user?.displayName ?? undefined;

  const isSuper = extra?.isSuperManager === true;

  const loadHub = useCallback(async (options?: { refresh?: boolean }) => {
    if (!userId) {
      setAccountInvitations([]);
      setEventInvitations([]);
      setAccounts([]);
      setEvents([]);
      setLoadError(null);
      setLoading(false);
      setRefreshing(false);
      return;
    }

    if (options?.refresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setLoadError(null);

    try {
      const [
        pendingAccountInvitations,
        pendingEventInvitations,
        accountMemberships,
        eventMemberships,
      ] = await Promise.all([
        getMyPendingAccountInvitations(),
        getMyPendingEventInvitations(),
        listMyAccountMemberships(userId),
        listMyEventMemberships(dataSource, userId),
      ]);

      setAccountInvitations(pendingAccountInvitations);
      setEventInvitations(pendingEventInvitations);
      setAccounts(
        accountMemberships
          .filter((membership) => membership.role === MANAGER_MEMBERSHIP_ROLE)
          .flatMap((membership) => {
            const accountId = accountIdFromRef(membership.accountRefPath);
            if (!accountId) {
              return [];
            }
            return [{ id: accountId, name: membership.account.name }];
          })
      );
      setEvents(
        eventMemberships
          .filter((membership) => membership.role === MANAGER_MEMBERSHIP_ROLE)
          .flatMap((membership) => {
            const ids = eventIdsFromRef(membership.eventRefPath);
            if (!ids) {
              return [];
            }
            return [
              {
                accountId: ids.accountId,
                eventId: ids.eventId,
                label: membership.event.name,
              },
            ];
          })
      );
    } catch (error) {
      console.warn("Failed to load admin hub", error);
      setAccountInvitations([]);
      setEventInvitations([]);
      setAccounts([]);
      setEvents([]);
      setLoadError(hubLoadErrorMessage(error));
    } finally {
      if (options?.refresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, [dataSource, userId]);

  const handleRefreshHub = useCallback(async () => {
    try {
      const extras = await getMyPermissions();
      authController.setExtra(
        mergeAuthExtras(authController.extra as AuthExtras | undefined, extras)
      );
    } catch (error) {
      console.warn("getMyPermissions failed", error);
    }
    await loadHub({ refresh: true });
  }, [authController, loadHub]);

  useEffect(() => {
    void loadHub();
  }, [loadHub]);

  const handleAcceptAccountInvitation = async (
    invitation: PendingAccountInvitation
  ) => {
    const key = `account:${invitation.accountId}:${invitation.invitationId}`;
    setAcceptingId(key);
    try {
      await acceptAccountInvitation(invitation.accountId, invitation.invitationId);
      const extras = await getMyPermissions();
      authController.setExtra(
        mergeAuthExtras(authController.extra as AuthExtras | undefined, extras)
      );
      await loadHub();
    } catch (error) {
      console.warn("acceptAccountInvitation failed", error);
    } finally {
      setAcceptingId(null);
    }
  };

  const handleAcceptEventInvitation = async (
    invitation: PendingEventInvitation
  ) => {
    const key = `event:${invitation.accountId}:${invitation.eventId}:${invitation.invitationId}`;
    setAcceptingId(key);
    try {
      await acceptEventInvitation(
        invitation.accountId,
        invitation.eventId,
        invitation.invitationId
      );
      const extras = await getMyPermissions();
      authController.setExtra(
        mergeAuthExtras(authController.extra as AuthExtras | undefined, extras)
      );
      await loadHub();
    } catch (error) {
      console.warn("acceptEventInvitation failed", error);
    } finally {
      setAcceptingId(null);
    }
  };

  const hasAccounts = accounts.length > 0;
  const hasEvents = events.length > 0;
  const invitationCount = accountInvitations.length + eventInvitations.length;
  const hasInvitations = invitationCount > 0;
  const hasPanels = isSuper || hasAccounts || hasEvents;
  const isEmpty = !loading && !loadError && !hasPanels && !hasInvitations;

  const intro = getHubIntroCopy({
    hasInvitations,
    hasPanels,
    isEmpty,
  });

  if (loading) {
    return (
      <div className="w-full">
        <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 px-6 py-12">
          <CircularProgress size="medium" />
          <Typography variant="body2" className={hubMuted}>
            Cargando tus paneles e invitaciones…
          </Typography>
        </div>
        <HubLoadingSkeleton />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-8 pb-20 sm:px-6 sm:py-10">
      <header className="relative mb-10 overflow-hidden rounded-2xl border border-surface-200/90 bg-gradient-to-br from-primary/[0.07] via-surface-50 to-surface-100 p-6 shadow-sm dark:border-surface-700 dark:from-primary/10 dark:via-surface-900 dark:to-surface-800">
        <div
          className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-primary/10 blur-2xl dark:bg-primary/20"
          aria-hidden
        />
        <div className="relative flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <div className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/90 p-1.5 shadow-sm ring-1 ring-surface-200/80 dark:bg-surface-800 dark:ring-surface-600 sm:flex">
              <img
                src={logoUrl}
                alt=""
                className="h-full w-full object-contain"
                aria-hidden
              />
            </div>
            <div className="min-w-0">
              <p className={`text-xs font-semibold uppercase tracking-wider text-primary`}>
                TeamOps Admin
              </p>
              <Typography
                variant="h5"
                className={`mt-1 text-2xl font-semibold tracking-tight sm:text-[1.65rem] ${hubBody}`}
              >
                {displayName ? `Hola, ${displayName}` : "Bienvenido"}
              </Typography>
              <p className={`mt-1.5 text-sm ${hubMuted}`}>
                Elegí un panel o gestioná tus invitaciones pendientes.
              </p>
            </div>
          </div>

          {!loadError && (hasInvitations || hasPanels) ? (
            <div className="flex flex-wrap gap-2 sm:justify-end">
              <HubStatPill label="invitaciones" value={invitationCount} />
              <HubStatPill label="cuentas" value={accounts.length} />
              <HubStatPill label="eventos" value={events.length} />
              {isSuper ? (
                <span className="inline-flex items-center rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-xs font-medium text-primary dark:border-primary/35 dark:bg-primary/15">
                  Super admin
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        {!loadError ? (
          <div
            className={`relative mt-5 space-y-2.5 rounded-xl border border-white/60 bg-white/70 p-4 backdrop-blur-sm dark:border-surface-600/60 dark:bg-surface-900/50`}
          >
            {intro.map((paragraph) => (
              <Typography
                key={paragraph}
                variant="body2"
                className={`leading-relaxed ${hubMuted}`}
              >
                {paragraph}
              </Typography>
            ))}
          </div>
        ) : null}
      </header>

      <div className="flex flex-col gap-10">
        {loadError ? (
          <Card className="overflow-hidden rounded-2xl border-red-300/70 bg-red-50 p-5 dark:border-red-800/70 dark:bg-red-950/40">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <Typography
                variant="body2"
                className="leading-relaxed text-red-800 dark:text-red-200"
              >
                {loadError}
              </Typography>
              <Button
                variant="outlined"
                size="medium"
                className="shrink-0 !rounded-xl !normal-case"
                onClick={() => void loadHub()}
              >
                Reintentar
              </Button>
            </div>
          </Card>
        ) : null}

        {hasInvitations ? (
          <HubSection
            title="Te invitaron"
            description="Revisá quién te invitó y aceptá para habilitar el acceso."
            badge={invitationCount}
          >
            <ul className="flex flex-col gap-3">
              {accountInvitations.map((invitation) => {
                const key = `account:${invitation.accountId}:${invitation.invitationId}`;
                const copy = formatAccountInvitationCopy(invitation);

                return (
                  <li key={key}>
                    <Card
                      className={`${hubCard} border-l-[3px] border-l-amber-400 p-0 dark:border-l-amber-500`}
                    >
                      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6 sm:p-5">
                        <div className="flex min-w-0 flex-1 gap-3.5">
                          <HubIconBadge tone="amber">
                            <MailIcon className="h-5 w-5" />
                          </HubIconBadge>
                          <div className="min-w-0 space-y-2">
                            <p
                              className={`text-base font-medium leading-snug ${hubBody}`}
                            >
                              {copy.message}
                            </p>
                            <p
                              className={`text-sm leading-relaxed ${hubMuted}`}
                            >
                              {copy.hint}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="filled"
                          size="medium"
                          className={hubAcceptButton}
                          disabled={acceptingId === key}
                          onClick={() =>
                            void handleAcceptAccountInvitation(invitation)
                          }
                        >
                          {acceptingId === key
                            ? "Un momento…"
                            : "Aceptar invitación"}
                        </Button>
                      </div>
                    </Card>
                  </li>
                );
              })}
              {eventInvitations.map((invitation) => {
                const key = `event:${invitation.accountId}:${invitation.eventId}:${invitation.invitationId}`;
                const copy = formatEventInvitationCopy(invitation);

                return (
                  <li key={key}>
                    <Card
                      className={`${hubCard} border-l-[3px] border-l-amber-400 p-0 dark:border-l-amber-500`}
                    >
                      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6 sm:p-5">
                        <div className="flex min-w-0 flex-1 gap-3.5">
                          <HubIconBadge tone="amber">
                            <MailIcon className="h-5 w-5" />
                          </HubIconBadge>
                          <div className="min-w-0 space-y-2">
                            <p
                              className={`text-base font-medium leading-snug ${hubBody}`}
                            >
                              {copy.message}
                            </p>
                            <p
                              className={`text-sm leading-relaxed ${hubMuted}`}
                            >
                              {copy.hint}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="filled"
                          size="medium"
                          className={hubAcceptButton}
                          disabled={acceptingId === key}
                          onClick={() =>
                            void handleAcceptEventInvitation(invitation)
                          }
                        >
                          {acceptingId === key
                            ? "Un momento…"
                            : "Aceptar invitación"}
                        </Button>
                      </div>
                    </Card>
                  </li>
                );
              })}
            </ul>
          </HubSection>
        ) : null}

        {hasPanels ? (
          <HubSection title="Tus accesos" description="Entrá al espacio que quieras administrar hoy.">
            <div className="flex flex-col gap-6 rounded-2xl border border-surface-200/80 bg-surface-100/50 p-4 dark:border-surface-700 dark:bg-surface-900/40 sm:p-5">
              {isSuper ? (
                <div className="flex flex-col gap-2.5">
                  <p className={`text-xs font-semibold uppercase tracking-wide ${hubMuted}`}>
                    Administración global
                  </p>
                  <HubLinkItem
                    to={buildGlobalPath()}
                    label="Panel global"
                    icon={<SettingsIcon className="h-5 w-5" />}
                  />
                </div>
              ) : null}

              {hasAccounts ? (
                <div className="flex flex-col gap-2.5">
                  <p className={`text-xs font-semibold uppercase tracking-wide ${hubMuted}`}>
                    Cuentas
                  </p>
                  <ul className="flex flex-col gap-2">
                    {accounts.map((account) => (
                      <li key={account.id}>
                        <HubLinkItem
                          to={buildAccountPath(account.id)}
                          label={account.name}
                          icon={<BusinessIcon className="h-5 w-5" />}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {hasEvents ? (
                <div className="flex flex-col gap-2.5">
                  <p className={`text-xs font-semibold uppercase tracking-wide ${hubMuted}`}>
                    Eventos
                  </p>
                  <ul className="flex flex-col gap-2">
                    {events.map((event) => (
                      <li key={`${event.accountId}:${event.eventId}`}>
                        <HubLinkItem
                          to={buildEventPath({
                            accountId: event.accountId,
                            eventId: event.eventId,
                            spaceId: "",
                          })}
                          label={event.label}
                          icon={<LinkIcon className="h-5 w-5" />}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </HubSection>
        ) : null}

        {isEmpty ? (
          <Card className={`${hubCard} px-6 py-10 text-center sm:px-10`}>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-surface-200/70 dark:bg-surface-700">
              <MailIcon className={`h-7 w-7 ${hubMuted}`} />
            </div>
            <Typography variant="h6" className={`mt-5 ${hubBody}`}>
              Sin accesos ni invitaciones por ahora
            </Typography>
            <Typography
              variant="body2"
              className={`mx-auto mt-2 max-w-md leading-relaxed ${hubMuted}`}
            >
              Si te acaban de invitar o te dieron permisos, puede demorar un
              momento. Verificá que iniciaste sesión con el correo correcto y
              actualizá la pantalla.
            </Typography>
            <Button
              variant="outlined"
              size="medium"
              disabled={refreshing}
              className="mx-auto mt-6 !rounded-xl !normal-case"
              onClick={() => void handleRefreshHub()}
            >
              {refreshing ? "Actualizando…" : "Actualizar"}
            </Button>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
